import React, { useState, useEffect } from 'react';
import api from '../../config/axios';
import ShadcnActionDropdown from '../common/ShadcnActionDropdown/ShadcnActionDropdown';
import './VendorApprovals.css';

import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';

const VendorApprovals = () => {
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [notes, setNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        fetchPendingApprovals();
    }, []);

    const fetchPendingApprovals = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/vendor-approvals/pending');
            setPendingApprovals(response.data.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching pending approvals:', err);
            setError('Failed to load pending vendor approvals. Please check your connection and try again.');
            setPendingApprovals([]);
        } finally {
            setLoading(false);
        }
    };

    const createTestVendors = async () => {
        try {
            setLoading(true);
            await api.post('/admin/vendor-approvals/create-test-vendors');
            alert('Test vendors created successfully!');
            fetchPendingApprovals(); // Refresh the list
        } catch (err) {
            console.error('Error creating test vendors:', err);
            alert('Failed to create test vendors. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (vendor) => {
        setSelectedVendor(vendor);
        setShowModal(true);
    };

    const handleApprove = async () => {
        if (!selectedVendor) return;

        try {
            setActionLoading(true);
            await api.post(`/admin/vendor-approvals/${selectedVendor.vendorId}/approve`, null, {
                params: {
                    adminId: localStorage.getItem('adminId') || 'admin',
                    notes: notes
                }
            });

            alert('Vendor approved successfully!');
            setShowModal(false);
            setSelectedVendor(null);
            setNotes('');
            fetchPendingApprovals(); // Refresh the list
        } catch (err) {
            console.error('Error approving vendor:', err);
            alert('Failed to approve vendor. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedVendor || !rejectionReason.trim()) {
            alert('Please provide a reason for rejection.');
            return;
        }

        try {
            setActionLoading(true);
            await api.post(`/admin/vendor-approvals/${selectedVendor.vendorId}/reject`, null, {
                params: {
                    adminId: localStorage.getItem('adminId') || 'admin',
                    rejectionReason: rejectionReason
                }
            });

            alert('Vendor rejected successfully!');
            setShowModal(false);
            setSelectedVendor(null);
            setRejectionReason('');
            fetchPendingApprovals(); // Refresh the list
        } catch (err) {
            console.error('Error rejecting vendor:', err);
            alert('Failed to reject vendor. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'PENDING': 'orange',
            'APPROVED': 'green',
            'REJECTED': 'red'
        };
        return (
            <span className={`status-badge ${statusColors[status] || 'gray'}`}>
                {status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="vendor-approvals-container">
                <LoadingSpinner message="Loading pending vendor approvals..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="vendor-approvals-container">
                <div className="error-container">
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={fetchPendingApprovals} className="retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="vendor-approvals-container">
            <div className="header">
                <h1>Vendor Approvals</h1>
                <p>Review and manage vendor approval requests</p>
                <button
                    onClick={createTestVendors}
                    className="create-test-btn"
                    style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginTop: '10px'
                    }}
                >
                    🧪 Create Test Vendors
                </button>
            </div>

            <div className="stats-cards">
                <div className="stat-card">
                    <h3>Pending Approvals</h3>
                    <p className="stat-number">{pendingApprovals.length}</p>
                </div>
            </div>

            <div className="approvals-list">
                <h2>Pending Applications</h2>

                {pendingApprovals.length === 0 ? (
                    <div className="empty-state">
                        <p>No pending vendor approvals at this time.</p>
                    </div>
                ) : (
                    <div className="approvals-grid">
                        {pendingApprovals.map((vendor) => (
                            <div key={vendor.vendorId} className="approval-card">
                                <div className="card-header">
                                    <h3>{vendor.businessName || 'Unnamed Business'}</h3>
                                    {getStatusBadge(vendor.approvalStatus)}
                                </div>

                                <div className="card-content">
                                    <div className="info-row">
                                        <span className="label">Vendor ID:</span>
                                        <span className="value">{vendor.vendorId}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Email:</span>
                                        <span className="value">{vendor.email || 'N/A'}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Contact:</span>
                                        <span className="value">{vendor.firstName} {vendor.lastName}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Address:</span>
                                        <span className="value">{vendor.address || 'N/A'}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Phone:</span>
                                        <span className="value">{vendor.phoneNumber || 'N/A'}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Submitted:</span>
                                        <span className="value">{formatDate(vendor.submittedAt)}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Documents:</span>
                                        <span className="value">{vendor.documents?.length || 0} uploaded</span>
                                    </div>
                                </div>

                                <div className="card-actions">
                                    <ShadcnActionDropdown
                                        size="small"
                                        variant="default"
                                        triggerText="Actions"
                                        actions={[
                                            {
                                                label: 'View Details',
                                                icon: 'view',
                                                onClick: () => handleViewDetails(vendor),
                                                tooltip: 'View vendor application details'
                                            }
                                        ]}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal for vendor details */}
            {showModal && selectedVendor && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Vendor Application Details</h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="close-btn"
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-content">
                            <div className="vendor-details">
                                <h3>{selectedVendor.businessName}</h3>

                                <div className="detail-section">
                                    <h4>Business Information</h4>
                                    <div className="detail-row">
                                        <span className="label">Vendor ID:</span>
                                        <span className="value">{selectedVendor.vendorId}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Address:</span>
                                        <span className="value">{selectedVendor.address || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Phone:</span>
                                        <span className="value">{selectedVendor.phoneNumber || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Submitted:</span>
                                        <span className="value">{formatDate(selectedVendor.submittedAt)}</span>
                                    </div>
                                </div>

                                <div className="detail-section">
                                    <h4>Documents</h4>
                                    {selectedVendor.documents && selectedVendor.documents.length > 0 ? (
                                        <div className="documents-list">
                                            {selectedVendor.documents.map((doc, index) => (
                                                <div key={index} className="document-item">
                                                    <div className="document-info">
                                                        <span className="document-name">{doc.documentName || doc.documentType}</span>
                                                        <span className="document-type">{doc.documentType}</span>
                                                        <span className={`document-status ${(doc.status || 'pending').toLowerCase()}`}>{doc.status || 'PENDING'}</span>
                                                    </div>
                                                    <div className="document-actions">
                                                        {doc.documentUrl && (
                                                            <>
                                                                <a
                                                                    href={doc.documentUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="view-document-btn"
                                                                    title="View Document"
                                                                >
                                                                    👁️ View
                                                                </a>
                                                                <a
                                                                    href={doc.documentUrl}
                                                                    download
                                                                    className="download-document-btn"
                                                                    title="Download Document"
                                                                >
                                                                    📥 Download
                                                                </a>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-documents">
                                            <p>⚠️ No documents uploaded</p>
                                            <p className="note">This vendor has not uploaded any documents yet.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="action-section">
                                <div className="approval-actions">
                                    <div className="approve-section">
                                        <h4>Approve Vendor</h4>
                                        <textarea
                                            placeholder="Add notes (optional)"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            className="notes-textarea"
                                        />
                                        <button
                                            onClick={handleApprove}
                                            disabled={actionLoading}
                                            className="approve-btn"
                                        >
                                            {actionLoading ? 'Approving...' : 'Approve Vendor'}
                                        </button>
                                    </div>

                                    <div className="reject-section">
                                        <h4>Reject Vendor</h4>
                                        <textarea
                                            placeholder="Reason for rejection (required)"
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            className="rejection-textarea"
                                            required
                                        />
                                        <button
                                            onClick={handleReject}
                                            disabled={actionLoading || !rejectionReason.trim()}
                                            className="reject-btn"
                                        >
                                            {actionLoading ? 'Rejecting...' : 'Reject Vendor'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorApprovals;
